import { useTranslation } from 'react-i18next'
import { X, Eye, PenLine, Shield, Crown, Star } from 'lucide-react'
import { usePermissions } from '../../hooks/usePermissions'
import type { MembershipRole } from '../../types'

interface RoleHelpPanelProps {
  open: boolean
  onClose: () => void
}

const roleConfig: Record<MembershipRole, { icon: typeof Eye; colorClass: string; descKey: string }> = {
  VIEWER: { icon: Eye, colorClass: 'text-slate-500 bg-slate-500/10', descKey: 'onboarding.roleViewer' },
  CONTRIBUTOR: { icon: PenLine, colorClass: 'text-blue-500 bg-blue-500/10', descKey: 'onboarding.roleContributor' },
  MANAGER: { icon: Shield, colorClass: 'text-emerald-500 bg-emerald-500/10', descKey: 'onboarding.roleManager' },
  ADMIN: { icon: Crown, colorClass: 'text-amber-500 bg-amber-500/10', descKey: 'onboarding.roleAdmin' },
  OWNER: { icon: Star, colorClass: 'text-purple-500 bg-purple-500/10', descKey: 'onboarding.roleOwner' },
}

export function RoleHelpPanel({ open, onClose }: RoleHelpPanelProps) {
  const { t } = useTranslation()
  const { role } = usePermissions()

  if (!open) return null

  const currentRole = role || 'VIEWER'
  const config = roleConfig[currentRole]
  const Icon = config.icon

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed left-64 top-0 bottom-0 z-50 w-80 bg-card border-r border-border shadow-xl animate-slide-in-right overflow-y-auto">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold text-foreground">{t('onboarding.helpTitle')}</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Current role */}
          <div className="rounded-lg border border-border p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${config.colorClass}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('onboarding.yourRole')}</p>
                <p className="text-sm font-semibold text-foreground">{currentRole}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t(config.descKey)}
            </p>
          </div>

          {/* Quick tips */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              {t('onboarding.quickTips')}
            </h3>
            <ul className="space-y-2">
              {(['tip1', 'tip2', 'tip3'] as const).map((key) => (
                <li key={key} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  {t(`onboarding.${key}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  )
}
